import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { ScoreSummary } from '@/shared/api/generated/scoring';

/* A quality score, or an honest blank. Never a number on its own.
 *
 * `docs/design/SCORING.md` states the rule this cell exists to obey: **coverage always accompanies the
 * score.** "A 98 on 4% coverage is a lie, and shipping the score without coverage is the product
 * telling that lie." So the value and its coverage are rendered together or not at all — there is no
 * branch here that produces a bare number.
 *
 * Three distinct blanks, and they are not interchangeable:
 *
 *   - no summary at all       -> never scored. An em dash, muted, with a title saying so.
 *   - summary, not defensible -> measured nothing. Says so, rather than showing 0.
 *   - the request failed      -> unknown. The catalog is still usable; this column is not.
 *
 * The second is the one that would be easy to get wrong. `value: null` means no rule produced
 * evidence; rendering it as 0 would rank a dataset nobody tested as the worst in the estate.
 */

export interface ScoreCellProps {
  readonly summary: ScoreSummary | undefined;
  /** True when the batch score request failed. Distinct from "no score for this dataset". */
  readonly unavailable?: boolean | undefined;
}

/* Bands rather than a continuous colour ramp. A ramp implies precision the number does not have — a
 * 71 and a 73 are the same operational fact — and three bands are what a steward acts on. Thresholds
 * live here rather than in CSS because they are a product decision, not a visual one. */
function toneFor(value: number): string {
  if (value >= 90) return 'var(--status-ok)';
  if (value >= 70) return 'var(--status-warn)';
  return 'var(--status-error)';
}

export function ScoreCell({
  summary,
  unavailable = false,
}: ScoreCellProps): ReactElement {
  const { t } = useTranslation();

  if (unavailable) {
    return (
      <span className="lq-muted" title={t('catalog.score.unavailable.meaning')}>
        {t('catalog.score.unavailable.label')}
      </span>
    );
  }

  if (summary === undefined) {
    return (
      <span className="lq-muted" title={t('catalog.score.never.meaning')}>
        {/* An em dash, not "0" and not "N/A". The row has nothing to say about quality yet. */}
        &mdash;
      </span>
    );
  }

  /* Server-computed, not re-derived. Whether a number may be shown as a score is a product rule, and
   * a client deciding it independently is a client that will eventually decide it differently. */
  if (!summary.defensible) {
    return (
      <span className="lq-muted" title={t('catalog.score.unmeasured.meaning')}>
        {t('catalog.score.unmeasured.label')}
      </span>
    );
  }

  const value = summary.value ?? 0;
  const coverage = summary.coverage_ratio ?? 0;

  return (
    <span className="lq-score">
      <span className="lq-score__value lumiq-numeric" style={{ color: toneFor(value) }}>
        {value.toFixed(1)}
      </span>
      {/* Not optional, not behind a tooltip, not smaller than the eye can read. The coverage is the
          qualifier that makes the number honest, so it sits beside it at the same weight. */}
      <span
        className="lq-score__coverage lumiq-numeric"
        title={t('catalog.score.coverage.meaning', {
          covered: summary.columns_with_a_rule,
          total: summary.columns_total,
        })}
      >
        {t('catalog.score.coverage.label', {
          percent: Math.round(coverage * 100),
          covered: summary.columns_with_a_rule,
          total: summary.columns_total,
        })}
      </span>
      {/* A partial execution measured some of what it set out to. Marked in the list, because
          otherwise an incomplete measurement sits unremarked beside complete ones. */}
      {!summary.evidence_complete && (
        <span className="lq-score__partial" title={t('catalog.score.partial.meaning')}>
          {t('catalog.score.partial.label')}
        </span>
      )}
    </span>
  );
}
